<template>
  <div>
    <v-tooltip
      v-if="!authorizedToBeEdited"
      class="locked-field"
      bottom
    >
      <template v-slot:activator="{ on }">
        <v-icon
          class="locked-icon"
          v-on="on"
        >
          lock
        </v-icon>
      </template>
      <span v-html="$t('message.error.notAuthorizedField')" />
    </v-tooltip>
    <v-combobox
      :counter="limit"
      :disabled="disabled"
      :items="items"
      :label="label"
      :loading="loading"
      :multiple="true"
      :search-input="searchText"
      :value="selection"
      v-bind="$attrs"
      @update:searchInput="searchByText"
      @blur="onBlur"
      @focus="$emit('focus', $event)"
      @input="onInput"
    >
      <template v-slot:append>
        <v-icon
          v-if="!disableSort && multiple"
          :disabled="(value && value.length <= 1) || disabled"
          @click.stop.prevent="openDialog('sort')"
        >
          low_priority
        </v-icon>
        <v-icon
          class="ml-3"
          @click.stop.prevent="openDialog('searcher')"
        >
          folder
        </v-icon>
      </template>
      <template
        v-if="hasSuggests"
        slot="append-outer"
      >
        <v-tooltip top>
          <v-btn
            slot="activator"
            class="suggest-tags__btn"
            color="secondary"
            plain
            depressed
            flat
            :loading="suggesting"
            @click="suggestQueryCallback"
          >
            {{ $t('message.taxonomy.suggestBtn') }}
          </v-btn>
          <span>{{ $t('message.taxonomy.suggestTooltip') }}</span>
        </v-tooltip>
      </template>

      <template
        slot="selection"
        slot-scope="{ item, parent, selected }"
      >
        <nx-taxonomy-selector-selection
          v-if="typeof item === 'object'"
          :selected="selected"

          v-bind="item"
          @input="parent.selectItem(item)"
        />
      </template>

      <template
        slot="item"
        slot-scope="{ item }"
      >
        <v-list-tile-avatar>
          <v-icon v-html="item.icon" />
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title
            v-html="$options.filters.highlight(item.text, searchText)"
          />
        </v-list-tile-content>
      </template>

      <template slot="no-data">
        <v-list-tile>
          <v-list-tile-avatar>
            <v-progress-circular
              v-if="loading"
              :width="2"
              indeterminate
              size="28"
            />
            <v-icon
              v-else
              v-html="noDataIcon"
            />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title v-html="noDataTitle" />
          </v-list-tile-content>
        </v-list-tile>
      </template>
    </v-combobox>


    <nx-entity-sortable-dialog
      v-if="dialogState.sort.render"
      :title="$t('message.entity.modal.sortableTitle', { entity: label })"
      :active="dialogState.sort.active"
      :items="internalValue"
      @dismiss="closeDialog('sort')"
      @input="acceptSearcherValue"
    />

    <nx-searcher-dialog
      v-if="dialogState.searcher.render"
      :active="dialogState.searcher.active"
      :entity-tabs="false"
      :filters="filters"
      :limit="limit"
      :text="searchText"
      :value="internalValue"
      entity="category"
      fix-toolbar
      title="Buscador"
      @dismiss="closeDialog('searcher')"
      @input="acceptSearcherValue"
    />
  </div>
</template>

<script>
import {
  debounce, difference, isNil, isNumber, isObject, isString, omitBy,
} from 'lodash';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import Authorizable from '@/modules/authorization/mixins/authorizable';
import { typeToIcon } from '@/modules/taxonomy/config';
import { toArray } from '@/utils';
import SuggestTagSelector from '@/modules/content/taxonomy/mixins/suggest-tag';
import api from '../api';
import NxTaxonomySelectorSelection from './components/Selection';
import NxEntitySortableDialog from '@/modules/entity/dialogs/EntitySortableDialog.vue';
import ManageDialogs, { initDialog } from '@/mixins/manage-dialogs';

/**
 * Tag Selector.
 *
 * This component allows the user to select tags. It also allows the user to create new tags on the
 * fly, just by writting its name and pressing enter. This is the main difference between this
 * component and the general Taxonomy Selector.
 *
 * @property {{}}              [auth]     - The authorization configuration for the different
 *                                        actions that the component may perform.
 * @property {boolean}         [disabled] - Whether the component is disabled or not.
 * @property {number}          [limit]    - Maximun number of tags that can be selected. If the prop
 *                                        is not given, no limit will be applied. If it equals to 1,
 *                                        the component current value will be replaced for the new
 *                                        one whenever the user selects a new tag. Else, the
 *                                        component will not allow the user to select more than the
 *                                        specified tags, even disabling the creation of new ones.
 * @property {number|number[]} value      - The component's value.
 */
export default {
  name: 'NxTagSelector',
  components: {
    NxEntitySortableDialog,
    NxSearcherDialog,
    NxTaxonomySelectorSelection,
  },
  extends: {},
  mixins: [Authorizable, SuggestTagSelector, ManageDialogs],
  props: {
    allowCreateTags: {
      type: Boolean,
      default: true,
      required: false,
    },
    additionalFilters: {
      type: Object,
      default: null,
    },
    auth: {
      type: Object,
      default() {
        return {
          actions: {
            ADD: null,
            DELETE: null,
            CREATE: null,
          },
          params: {},
        };
      },
    },
    disabled: Boolean,
    limit: {
      type: Number,
      validator: val => val > 0,
      default: null,
    },
    value: {
      type: [Number, Object, Array],
      required: false,
      default: null,
    },
    siteId: {
      type: [Number, Array],
      default: null,
    },
    mediaId: {
      type: [Number, Array],
      default: null,
    },
    label: {
      type: String,
      default: undefined,
    },
    disableSort: Boolean,
  },
  data() {
    return {
      /**
       * @property {TaxonomyCard[]} cards - The cards of the taxonomies listed in the component.
       */
      cards: [],
      dialogState: {
        searcher: initDialog(),
        sort: initDialog(),
      },
      /**
       * @property {{}} filters - The filters used when searching.
       */
      filters: {
        type: ['tag'],
        siteId: this.siteId || this.$store.getters.siteId,
        mediaId: this.mediaId,
      },
      /**
       * @property {TaxonomyCard[]} internalValue - The internal representation of the component's value.
       */
      internalValue: [],
      /**
       * @property {number} loadingCount - Auxiliar variable used to know the component's loading state.
       */
      loadingCount: 0,
      /**
       * @property {string} searchText - The current search text.
       */
      searchText: null,
      /**
       * @property {{}} searchTokenSource - The search's token source.
       */
      searchTokenSource: {},
      isAuthorizedToCreate: false,
      searchOnFiltersChange: false,
    };
  },
  computed: {
    /**
     * Items displayed as options in VSelect.
     */
    items() {
      if (this.loading || this.limitReachedAndMultiple) return [];
      return this.cards.map(this.cardToItem);
    },
    /**
     * Whether the given limit has been reached or not.
     */
    limitReached() {
      return this.limit && this.internalValue.length >= this.limit;
    },
    limitReachedAndMultiple() {
      return this.limitReached && this.multiple;
    },
    loading: {
      set(val) {
        if (val) {
          this.loadingCount += 1;
        } else {
          this.loadingCount = Math.max((this.loadingCount - 1), 0);
        }
      },
      get() {
        return this.loadingCount > 0;
      },
    },
    /**
     * Icon used in the no-data slot.
     */
    noDataIcon() {
      if (this.limitReachedAndMultiple) return 'not_interested';
      if (this.filters.text !== undefined) return 'add';
      return 'edit';
    },
    /**
     * Title used in the no-data slot.
     */
    noDataTitle() {
      if (this.loading) return this.$t('message.common.loading');
      if (this.limitReachedAndMultiple) return this.$t('message.limitReached');
      if (this.filters.text && this.isAuthorizedToCreate && this.allowCreateTags) {
        return this.$t('message.taxonomy.selector.pressEnterToCreateTag', { tag: this.searchText });
      }
      return this.$t('message.search.typeToSearch');
    },
    /**
     * Whether the component allow multple values, that is, when the `limit` prop has been given and
     * is greater than 1.
     */
    multiple() {
      return this.limit !== 1;
    },
    /**
     * The selection, used as value for the VSelect component.
     */
    selection() {
      return this.internalValue.map(this.cardToItem);
    },
  },
  watch: {
    /**
     * Whenever the value changes, recompute the internal value.
     * The functions searches for the cards of the given taxonomies inside its internal state. If
     * they are not found, it fetches the information from the server.
     */
    value: {
      immediate: true,
      async handler(value) {
        const values = toArray(value);
        this.loading = true;
        const internalValue = await Promise.all(values.map(async (val) => {
          if (isNumber(val) || isString(val)) {
            const item = this.findCard(val);
            if (item) {
              val = item;
            } else {
              val = await this.fetchTaxonomy(val);
            }
          }
          if (!isObject(val)) return false;
          return val;
        }));
        this.internalValue = internalValue.filter(val => !!val);
        this.loading = false;
      },
    },
  },
  created() {
    if (isObject(this.additionalFilters)) {
      this.filters = { ...this.filters, ...this.additionalFilters };
    }
  },
  mounted() {
    if (this.auth.actions.CREATE) {
      this.checkCreationAuth();
    }
  },
  methods: {
    acceptSearcherValue(selectedTags) {
      this.cards = [...this.cards, ...selectedTags];
      this.onInput(selectedTags.map(this.cardToItem));
    },
    /**
     * Checks if the given action can be performed for the given taxonomies.
     * @param {string} action
     * @param {number[]} taxonomies
     * @return {Promise<boolean>}
     */
    async authorizeOperation(action, taxonomies) {
      if (!Array.isArray(taxonomies)
        || taxonomies.length === 0
        || !action) {
        return true;
      }
      this.loading = true;
      const results = await Promise.all(taxonomies.map(t => this.can(action, {
        taxonomyId: t,
        ...this.auth.params,
      })));
      this.loading = false;
      return results.every(result => result === true);
    },
    /**
     * Cancels the current search operation.
     * @param {string} reason
     */
    cancelCurrentSearch(reason = '') {
      if (this.searchTokenSource.cancel) this.searchTokenSource.cancel(reason);
      this.searchTokenSource = {};
    },
    /**
     * Transforms a TaxonomyCard to an item, to be displayed as option in the VSelect.
     * @param {TaxonomyCard} taxonomyCard
     * @return {{value: *, icon: *, text: string}}
     */
    cardToItem(taxonomyCard) {
      return {
        value: taxonomyCard.id,
        icon: taxonomyCard.icon || typeToIcon(taxonomyCard.type),
        text: (taxonomyCard.ancestors || [])
          .concat({ name: taxonomyCard.name })
          .map(t => t.name)
          .join(' / '),
      };
    },
    /**
     * Checks if the user can create new tags and, if so, creates a new one with the given name.
     * @param {string} name
     * @return {Promise<TaxonomyCard|boolean|null>} False if the user is not authorized to create new
     * tags, null if an error occurred while creating the entity, or a TaxonomyCard if the tag was
     * created successfully.
     */
    async createEntity(name) {
      if (!name || !this.allowCreateTags) return null;
      if (!this.isAuthorizedToCreate) return false;
      this.loading = true;

      try {
        return await api.create({ name }, 'tag');
      } catch (e) {
        console.error(`Error creating tag with name '${name}'`, e);
        return null;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Fetches the information of the given taxonomy identifier.
     * @param {number} taxonomyId
     * @return {Promise<TaxonomyCard|boolean>} A TaxonomyCard if the information was fetched
     * successfully; else, false will be returned.
     */
    async fetchTaxonomy(taxonomyId) {
      try {
        return await api.fetch(taxonomyId);
      } catch (e) {
        return false;
      }
    },
    findCard(id) {
      return this.cards.find(card => card.id === id)
        || this.internalValue.find(card => card.id === id);
    },
    onBlur($event) {
      this.searchText = null;
      delete this.filters.text;
      this.$emit('blur', $event);
    },
    /**
     * Modify the current component's value and emits the new one.
     * @param value
     * @return {Promise<void>}
     */
    async onInput(value) {
      this.searchText = null;
      delete this.filters.text;
      value = toArray(value);
      if (this.limitReachedAndMultiple && value.length > this.selection.length) {
        // The limit has been reached when component allows multple tags.
        return;
      }
      if (this.loading) {
        // Avoid modify the value while performing a search or asking for authorization.
        return;
      }
      this.loading = true;

      // If the user wants to create a new tag, it will be given as a string as the last value of
      // the given array.
      const taxonomyToCreate = value.length > 0 && isString(value[value.length - 1])
        ? value[value.length - 1] : null;
      if (taxonomyToCreate) value.splice(-1);

      // Determine which taxonomies are being added, which ones are being removed, and which ones
      // should be kept.
      const taxonomiesToAdd = difference(value, this.selection);
      const taxonomiesToDelete = difference(this.selection, value);
      let taxonomiesToKeep = difference(value, taxonomiesToAdd.concat(taxonomiesToDelete));

      // Try to create the new taxonomy, if given
      const taxonomyCreated = await this.createEntity(taxonomyToCreate);
      if (taxonomyCreated) {
        taxonomiesToAdd.push(this.cardToItem(taxonomyCreated)); // Created, add it to the taxonomies to be added.
      } else if (taxonomyCreated === false) { // Unauthorized
        this.$emit('authorized', false);
      }

      // Ask for permisions to perform the operations.
      const [authorizedToAdd, authorizedToDelete] = await Promise.all([
        this.authorizeOperation(this.auth.actions.ADD, taxonomiesToAdd.map(t => t.value)),
        this.authorizeOperation(this.auth.actions.DELETE, taxonomiesToDelete.map(t => t.value)),
      ]);

      if (authorizedToAdd) {
        taxonomiesToKeep = taxonomiesToKeep.concat(taxonomiesToAdd);
      } else {
        this.$emit('authorized', false);
      }

      if (!authorizedToDelete) {
        taxonomiesToKeep = taxonomiesToKeep.concat(taxonomiesToDelete);
        this.$emit('authorized', false);
      }

      // Emit the new value.
      if (this.multiple) {
        this.$emit('input', taxonomiesToKeep.map(item => item.value), taxonomiesToKeep);
      } else if (taxonomiesToKeep.length === 0) {
        this.$emit('input', null, null);
      } else {
        const card = this.findCard(taxonomiesToKeep.slice(-1)[0].value);
        this.$emit('input', (card || { id: null }).id, card || null);
      }
      this.loading = false;
    },
    /**
     * Whenever the search input changes, modify the search filters and perform a new search.
     */
    searchByText(value) {
      if (value === this.searchText) {
        return;
      }
      const oldSearchText = this.searchText;
      this.searchText = value;
      if (value !== '' && value !== null) {
        this.filters.text = value;
      } else {
        delete this.filters.text;
      }
      if (!value && !oldSearchText) {
        return;
      }
      this.performSearch();
    },
    /**
     * Performs a textual search. If a search is being performed currently, cancels it before.
     */
    // eslint-disable-next-line
    performSearch: debounce(async function () {
      if (this.limitReachedAndMultiple) return;
      this.loading = true;
      const response = await api.search(omitBy(this.filters, isNil));
      this.loading = false;
      if (!response
        || response.canceled
        || !Array.isArray(response.results)
      ) {
        return;
      }

      this.cards = response.results;
    }, 300),
    async checkCreationAuth() {
      this.isAuthorizedToCreate = await this.can(this.auth.actions.CREATE, {
        entity: 'taxonomy',
        type: 'tag',
      });
    },
  },
};
</script>


<style scoped>
/* La versión de Vuetify que se está usando (1.5.x) no admite márgenes negativos */
.suggest-tags__btn {
  margin: 0;
  margin-top: -.4rem;
}

.locked-icon {
  float: left;
  margin-top: 20px;
  padding-right: 5px;
}
</style>
